import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from '@firebase/auth';
import { FaPlus, FaTrash } from 'react-icons/fa';
import UserManagement from '../admin/UserManagement';
import DepartmentManagement from '../admin/DepartmentManagement';
import CategoryManagement from '../admin/CategoryManagement';
import TypeManagement from '../admin/TypeManagement';
import { db } from '../../firebase/config';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import './Management.css';

function Management() {
  const [activeTab, setActiveTab] = useState('locations');
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [groups, setGroups] = useState([]);
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
      setAuthError(null);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fixAdminLocation = async () => {
      try {
        const locationsRef = collection(db, 'locations');
        const snapshot = await getDocs(locationsRef);
        const adminLocation = snapshot.docs.find(doc => 
          doc.data().name === 'Admn' || 
          doc.id === '27gQp4BHDD8lHpESiyed'
        );

        if (adminLocation && adminLocation.data().name !== 'Admin') {
          await updateDoc(doc(db, 'locations', adminLocation.id), {
            name: 'Admin',
            updatedAt: serverTimestamp()
          });
          console.log('Updated admin location name to "Admin"');
        }
      } catch (error) {
        console.error('Error fixing admin location:', error);
      }
    };

    fixAdminLocation();
  }, []);

  useEffect(() => {
    fetchLocations();
    fetchDepartments();
    fetchGroups();
  }, []);

  useEffect(() => {
    const updateLocationTypes = async () => {
      try {
        // Update locations with current department names
        const updatedLocations = await Promise.all(locations.map(async location => {
          if (location.typeId) {
            const department = departments.find(d => d.id === location.typeId);
            if (department && department.name !== location.type) {
              await updateDoc(doc(db, 'locations', location.id), {
                type: department.name,
                updatedAt: serverTimestamp()
              });
              return { ...location, type: department.name };
            }
          }
          return location;
        }));
        setLocations(updatedLocations);
      } catch (error) {
        console.error('Error updating location types:', error);
      }
    };

    if (departments.length > 0 && locations.length > 0) {
      updateLocationTypes();
    }
  }, [departments]);

  const fetchLocations = async () => {
    try {
      const locationsRef = collection(db, 'locations');
      const snapshot = await getDocs(locationsRef);
      const locationsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLocations(locationsList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const departmentsRef = collection(db, 'departments');
      const snapshot = await getDocs(departmentsRef);
      const departmentsList = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(department => department.status === 'active');
      setDepartments(departmentsList);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      const groupsRef = collection(db, 'groups');
      const snapshot = await getDocs(groupsRef);
      const groupsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setGroups(groupsList);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const handleAdd = async (type) => {
    if (!isAuthenticated) {
      setAuthError('You must be logged in to perform this action');
      return;
    }

    if (type === 'location') {
      try {
        const newLocation = {
          name: 'New Location',
          type: '',
          typeId: '',
          status: 'active',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        const docRef = await addDoc(collection(db, 'locations'), newLocation);
        const locationWithId = { ...newLocation, id: docRef.id };
        setLocations([locationWithId, ...locations]);
      } catch (error) {
        console.error('Error adding location:', error);
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'locations', id));
      setLocations(locations.filter(loc => loc.id !== id));
    } catch (error) {
      console.error('Error deleting location:', error);
    }
  };

  const handleStatusToggle = async (locationId) => {
    try {
      const location = locations.find(loc => loc.id === locationId);
      const newStatus = location.status === 'active' ? 'inactive' : 'active';
      
      await updateDoc(doc(db, 'locations', locationId), { 
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      
      setLocations(locations.map(location =>
        location.id === locationId
          ? { ...location, status: newStatus }
          : location
      ));
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleTypeChange = async (locationId, departmentId) => {
    try {
      // Find the department to get its name
      const department = departments.find(d => d.id === departmentId);
      if (!department) return;

      await updateDoc(doc(db, 'locations', locationId), { 
        type: department.name,
        typeId: departmentId, // Store the department ID for reference
        updatedAt: serverTimestamp()
      });
      
      setLocations(locations.map(location =>
        location.id === locationId
          ? { ...location, type: department.name, typeId: departmentId }
          : location
      ));
    } catch (error) {
      console.error('Error updating department:', error);
    }
  };

  const handleNameChange = async (locationId, newName) => {
    try {
      await updateDoc(doc(db, 'locations', locationId), { 
        name: newName,
        updatedAt: serverTimestamp()
      });
      
      setLocations(locations.map(location =>
        location.id === locationId
          ? { ...location, name: newName }
          : location
      ));
    } catch (error) {
      console.error('Error updating name:', error);
    }
  };

  const handleNameEdit = (locationId, isEditing) => {
    setLocations(locations.map(location =>
      location.id === locationId
        ? { ...location, isEditing }
        : location
    ));
  };

  const handleAddGroup = async () => {
    if (!isAuthenticated) {
      setAuthError('You must be logged in to perform this action');
      return;
    }

    try {
      const newGroup = {
        name: 'New Group',
        description: '',
        status: 'active',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, 'groups'), newGroup);
      const groupWithId = { ...newGroup, id: docRef.id };
      setGroups([groupWithId, ...groups]);
    } catch (error) {
      console.error('Error adding group:', error);
    }
  };

  const handleDeleteGroup = async (id) => {
    try {
      await deleteDoc(doc(db, 'groups', id));
      setGroups(groups.filter(group => group.id !== id));
    } catch (error) {
      console.error('Error deleting group:', error);
    }
  };

  const handleGroupStatusToggle = async (groupId) => {
    try {
      const group = groups.find(g => g.id === groupId);
      const newStatus = group.status === 'active' ? 'inactive' : 'active';
      
      await updateDoc(doc(db, 'groups', groupId), { 
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      
      setGroups(groups.map(group =>
        group.id === groupId
          ? { ...group, status: newStatus }
          : group
      ));
    } catch (error) {
      console.error('Error updating group status:', error);
    }
  };

  const handleGroupFieldChange = async (groupId, field, value) => {
    try {
      await updateDoc(doc(db, 'groups', groupId), { 
        [field]: value,
        updatedAt: serverTimestamp()
      });
      
      setGroups(groups.map(group =>
        group.id === groupId
          ? { ...group, [field]: value }
          : group
      ));
    } catch (error) {
      console.error(`Error updating group ${field}:`, error);
    }
  };

  const handleGroupFieldEdit = (groupId, field, isEditing) => {
    setGroups(groups.map(group =>
      group.id === groupId
        ? { ...group, [`${field}Editing`]: isEditing }
        : group
    ));
  };

  const LocationsContent = () => (
    <div className="data-container">
      <button className="add-btn" onClick={() => handleAdd('location')}>
        <FaPlus /> Add Location
      </button>
      <table className="data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Department</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {locations.map(location => (
            <tr key={location.id}>
              <td>
                {location.isEditing ? (
                  <input
                    type="text"
                    value={location.name}
                    onChange={(e) => handleNameChange(location.id, e.target.value)}
                    onBlur={() => handleNameEdit(location.id, false)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleNameEdit(location.id, false);
                      }
                    }}
                    autoFocus
                    className="name-input"
                  />
                ) : (
                  <span 
                    onClick={() => handleNameEdit(location.id, true)}
                    className="editable-name"
                  >
                    {location.name}
                  </span>
                )}
              </td>
              <td>
                <select
                  value={location.typeId || ''}
                  onChange={(e) => handleTypeChange(location.id, e.target.value)}
                  className="type-select"
                >
                  <option value="">Select Department</option>
                  {departments
                    .filter(dept => dept.status === 'active')
                    .map(department => (
                      <option key={department.id} value={department.id}>
                        {department.name}
                      </option>
                  ))}
                </select>
              </td>
              <td>
                <button 
                  className={`status-badge ${location.status}`}
                  onClick={() => handleStatusToggle(location.id)}
                >
                  {location.status}
                </button>
              </td>
              <td>
                <button className="delete-button" onClick={() => handleDelete(location.id)}>
                  <FaTrash />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="management-container">
      <div className="management-nav">
        <button 
          className={`nav-tab ${activeTab === 'locations' ? 'active' : ''}`}
          onClick={() => setActiveTab('locations')}
        >
          Locations
        </button>
        <button 
          className={`nav-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button 
          className={`nav-tab ${activeTab === 'departments' ? 'active' : ''}`}
          onClick={() => setActiveTab('departments')}
        >
          Departments
        </button>
        <button 
          className={`nav-tab ${activeTab === 'categories' ? 'active' : ''}`}
          onClick={() => setActiveTab('categories')}
        >
          Types
        </button>
        <button 
          className={`nav-tab ${activeTab === 'groups' ? 'active' : ''}`}
          onClick={() => setActiveTab('groups')}
        >
          Groups
        </button>
      </div>

      {authError && (
        <div className="auth-error">
          {authError}
        </div>
      )}

      <div className="management-content">
        {activeTab === 'locations' ? (
          <LocationsContent />
        ) : activeTab === 'users' ? (
          <UserManagement />
        ) : activeTab === 'departments' ? (
          <DepartmentManagement />
        ) : activeTab === 'categories' ? (
          <TypeManagement />
        ) : (
          <div>
            <button className="add-btn" onClick={handleAddGroup}>
              <FaPlus /> Add Group
            </button>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {groups.map(group => (
                  <tr key={group.id}>
                    <td>
                      {group.nameEditing ? (
                        <input
                          type="text"
                          value={group.name}
                          onChange={(e) => handleGroupFieldChange(group.id, 'name', e.target.value)}
                          onBlur={() => handleGroupFieldEdit(group.id, 'name', false)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleGroupFieldEdit(group.id, 'name', false);
                            }
                          }}
                          autoFocus
                          className="name-input"
                        />
                      ) : (
                        <span 
                          onClick={() => handleGroupFieldEdit(group.id, 'name', true)}
                          className="editable-name"
                        >
                          {group.name}
                        </span>
                      )}
                    </td>
                    <td>
                      {group.descriptionEditing ? (
                        <input
                          type="text"
                          value={group.description}
                          onChange={(e) => handleGroupFieldChange(group.id, 'description', e.target.value)}
                          onBlur={() => handleGroupFieldEdit(group.id, 'description', false)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleGroupFieldEdit(group.id, 'description', false);
                            }
                          }}
                          autoFocus
                          className="name-input"
                        />
                      ) : (
                        <span 
                          onClick={() => handleGroupFieldEdit(group.id, 'description', true)}
                          className="editable-name"
                        >
                          {group.description}
                        </span>
                      )}
                    </td>
                    <td>
                      <button 
                        className={`status-badge ${group.status}`}
                        onClick={() => handleGroupStatusToggle(group.id)}
                      >
                        {group.status}
                      </button>
                    </td>
                    <td>
                      <button className="delete-button" onClick={() => handleDeleteGroup(group.id)}>
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Management;